import { z } from "zod"
import { CommunicationChannel } from "../../common/CommunicationChannel"
import { emailSchema } from "../../common/email-validator"
import { JobStatus } from "../../service/job"

export const MessageChannelSchema = z.enum([
  CommunicationChannel.SMS,
  CommunicationChannel.EMAIL,
])

export type MessageChannel = z.infer<typeof MessageChannelSchema>

export const ScheduleMessageAsStoreStaffSchema = z
  .object({
    jobId: z.guid(),
    content: z.string().min(1),
    // multipart/form-data does not carry repeated values reliably, so the
    // channels arrive as a comma-separated string and are split back here.
    channels: z
      .string()
      .transform((value) => value.split(","))
      .pipe(MessageChannelSchema.array().min(1).max(2)),
    toPhoneNumber: z.string().min(1).optional(),
    toEmail: emailSchema().optional(),
    // Applied by the API when the send is delivered, not when it is scheduled —
    // undoing the message must leave the job's status untouched.
    statusToSetOnSend: z.nativeEnum(JobStatus).optional(),
    // The mechanic asked for the message text itself in the SMS rather than the
    // link teaser, accepting that a long message costs several segments. Only a
    // request: the API still delivers the full text regardless when it fits in
    // one segment, since that costs the store nothing extra.
    // multipart/form-data carries booleans as strings.
    sendFullSms: z
      .enum(["true", "false"])
      .transform((value) => value === "true")
      .optional(),
    // attachedFiles handled by multer, not in validation
  })
  .refine(
    (value) =>
      !value.channels.includes(CommunicationChannel.SMS) ||
      value.toPhoneNumber !== undefined,
    { path: ["toPhoneNumber"] },
  )
  .refine(
    (value) =>
      !value.channels.includes(CommunicationChannel.EMAIL) ||
      value.toEmail !== undefined,
    { path: ["toEmail"] },
  )

export type ScheduleMessageAsStoreStaff = z.infer<
  typeof ScheduleMessageAsStoreStaffSchema
>

export interface ScheduledMessageGroup {
  scheduledSendGroupId: string
  scheduledSendAt: Date
  messageIds: string[]
}

export interface CancelledScheduledMessage {
  content: string
  channels: MessageChannel[]
}

export interface DispatchedScheduledMessage {
  dispatched: boolean
}
